import React, { useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
} from 'react-bootstrap';
import '../../styles/styles.css';
import '../../styles/custom-fixes.css';
import { useThemeMode } from '../../ThemeContext';
import { dataAPI } from '../../helpers/api_service';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';

import SiteHeader from '../../components/SiteHeader';
import Footer from './Footer';
import './VideoTutorial.css';
import useSeoMeta from '../SeoPages/useSeoMeta';
import { getPublicSeoPage } from '../../seo/publicPages';

function VideoTutorial() {
  const { mode } = useThemeMode();
  useSeoMeta(getPublicSeoPage('/learning-hub/'));
  const [videos, setVideos] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [selectedVideo, setSelectedVideo] = React.useState(null);

  useEffect(() => {
    document.body.classList.remove('dark-mode', 'light-mode');
    document.body.classList.add(mode === 'dark' ? 'dark-mode' : 'light-mode');
  }, [mode]);

  const extractYoutubeId = (url) => {
    if (!url) return '';
    try {
      const parsed = new URL(url);
      if (parsed.hostname === 'youtu.be') {
        return parsed.pathname.replace('/', '');
      }
      const vParam = parsed.searchParams.get('v');
      if (vParam) return vParam;
      const segments = parsed.pathname.split('/').filter(Boolean);
      return segments[segments.length - 1] || '';
    } catch (e) {
      console.error('Invalid YouTube URL:', url, e);
      return '';
    }
  };

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await dataAPI.getVideoTutorials({ showLoading: true, loadingMessage: 'Loading tutorials...' });
        if (response.status === 'success' && Array.isArray(response.data)) {
          const mapped = response.data.map((item) => ({
            id: item.id,
            title: item.title,
            description: item.description,
            youtubeUrl: item.video_link,
            videoId: extractYoutubeId(item.video_link),
            duration: '',
            category: 'Tutorial',
            createdAt: item.created_at,
          }));
          setVideos(mapped);
        } else {
          setError('Failed to load video tutorials.');
        }
      } catch (err) {
        console.error('Error fetching video tutorials:', err);
        setError('Failed to load video tutorials.');
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  useEffect(() => {
    if (selectedVideo) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [selectedVideo]);

  useEffect(() => {
    if (!selectedVideo) return;
    const onEscape = (e) => {
      if (e.key === 'Escape') closeVideo();
    };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [selectedVideo]);

  const getThumbnailUrl = (videoId) =>
    `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;

  const openVideo = (e, video) => {
    e.preventDefault();
    setSelectedVideo(video);
  };

  const closeVideo = () => setSelectedVideo(null);

  return (
    <div
      style={{
        backgroundColor: mode === 'dark' ? '#111' : '#fff',
        minHeight: '100vh',
      }}
    >
      <SiteHeader activeNav="learning-hub" />

      <section
        className={`video-tutorial-section ${mode === 'dark' ? 'video-tutorial-dark' : ''}`}
      >
        <Container className="py-5">
          <div className="video-tutorial-header text-center mb-5">
            <h1 className="video-tutorial-title">Learning Hub</h1>
            <p className="video-tutorial-subtitle">
              Video tutorials to get the most out of Complimate. From setup to audits, policies, and reports.
            </p>
          </div>

          {loading && (
            <p className="video-tutorial-status text-center">Loading tutorials...</p>
          )}

          {error && !loading && (
            <p className="video-tutorial-status video-tutorial-status-error text-center">
              {error}
            </p>
          )}

          {!loading && !error && videos.length === 0 && (
            <p className="video-tutorial-status text-center">
              No video tutorials available yet.
            </p>
          )}

          {!loading && !error && videos.length > 0 && (
            <Row className="g-4">
              {videos.map((video) => (
                <Col key={video.id} xs={12} sm={6} lg={4}>
                  <Card className="video-tutorial-card h-100">
                    <button
                      type="button"
                      className="video-tutorial-thumb-link"
                      onClick={(e) => openVideo(e, video)}
                      aria-label={`Play ${video.title}`}
                    >
                      <div className="video-tutorial-thumb-wrap">
                        <img
                          src={getThumbnailUrl(video.videoId)}
                          alt={video.title}
                          className="video-tutorial-thumb"
                        />
                        <div className="video-tutorial-play-overlay">
                          <PlayCircleOutlineIcon className="play-icon" />
                        </div>
                        {video.videoId && (
                          <span className="video-tutorial-duration">
                            {video.duration || 'Watch'}
                          </span>
                        )}
                      </div>
                    </button>
                    <Card.Body className="video-tutorial-card-body">
                      <Card.Title className="video-tutorial-card-title">
                        {video.title}
                      </Card.Title>
                      <Card.Text className="video-tutorial-card-desc">
                        {video.description}
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Container>
      </section>

      {selectedVideo && (
        <div
          className="video-tutorial-modal-overlay"
          onClick={closeVideo}
          role="dialog"
          aria-modal="true"
          aria-label="Video player"
        >
          <div
            className="video-tutorial-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="video-tutorial-modal-close"
              onClick={closeVideo}
              aria-label="Close video"
            >
              ×
            </button>
            <div className="video-tutorial-modal-title">{selectedVideo.title}</div>
            <div className="video-tutorial-modal-embed-wrap">
              <iframe
                title={selectedVideo.title}
                src={`https://www.youtube.com/embed/${selectedVideo.videoId}?autoplay=1`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="video-tutorial-modal-iframe"
              />
            </div>
            <a
              href={selectedVideo.youtubeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="video-tutorial-modal-youtube-link"
            >
              Open in YouTube
            </a>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default VideoTutorial;
